import { Add } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  TextField,
  Autocomplete,
  Skeleton,
  Pagination,
  useMediaQuery,
  Chip,
  DialogTitle
} from "@mui/material";
import {
  autostoreTheme,
  BigChoiceModal,
  ConfirmationModal,
  formatUtcDate,
  ScanningIndicator,
  useScanIndicator,
  AutostoreTable
} from "@qubit/autoparts";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { getUserClientId } from "~/api/usersTypes/auth0Profile";
import { warehouseService } from "~/api/warehouse";
import { useAppDispatch, useAppSelector } from "~/app/store";

import { createProductSearchOptions } from "~/config/searchProductConfig";
import { InventoryAddDialog } from "~/features/inventoryAddDialog/InventoryAddDialog";
import InventoryAdjustDialog, {
  InventoryToModify
} from "~/features/inventoryAdjustDialog/InventoryAdjustDialog";
import { InventoryHoldModal } from "~/features/inventoryHoldModal/InventoryHoldModal";
import { InventoryProductCard } from "~/features/inventoryProductCard/InventoryProductCard";

import { useClientConfig } from "~/hooks/useClientConfig";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";

import {
  generateLocationNameFromBin,
  generateLocationNameFromInventorySummary,
  inventoryDateLabel,
  temperatureZoneColors,
  checkIsExpiration,
  getVariantDisplayNameByDtoFe,
  getHoldTypeOptions,
  searchProduct,
  HoldType
} from "~/lib/helpers";
import { TempZone } from "~/redux/actions/batch";
import {
  getInventorySummaries,
  getVariantByVariantId,
  getSlottingInfo,
  getBinById,
  getSlotBinById,
  clearSelectedVariant,
  clearSelectedInventoryId,
  clearInventory,
  updateSlottingInfo,
  createBinSlot,
  clearSlotBin,
  getInventoryReport,
  placeInventoryHold,
  clearInventoryToModify
} from "~/redux/actions/inventory";
import { InventoryHoldReasonCode } from "~/redux/public/inventory.openApi";
import { selectInventorySummaryToDisplay } from "~/redux/selectors/manualInventorySelectors";
import { useGetBinConfigurationsQuery } from "~/redux/warehouse/bin.hooks";
import { SearchBinRecord, InventorySummaryDto } from "~/types/api";

import { ManualInventorySearch } from "./ManualInventorySearch";
import { ManualInventoryToolbar } from "./ManualInventoryToolbar";
import { createBinSearchOptions } from "./createBinSearchOptions";
import {
  selectRows,
  selectSummaries,
  setGetBinsIndex,
  setIsAddPanelOpen,
  setIsAdjustPanelOpen,
  setIsBinComponentShown,
  setIsBinHoldModalOpen,
  setPortPollingActive
} from "./manualInventory.slice";
import { useSearchBinsByText } from "./useSearchBinsByText";
import { getViewType } from "./viewType";

export type BinOrProductResult = {
  type: "bin" | "product";
  variantId?: Guid;
  binId?: Guid;
  displayText: string;
  autostore_compartment_number?: number;
};

export function ManualInventory({
  viewTitle
}: {
  viewTitle?: ViewNameTranslation;
}) {
  const { data: availableBinConfigurations } = useGetBinConfigurationsQuery();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const locationInfo = useLocation();
  const { pathname, search } = locationInfo;
  const { variantId: variantIdParam, binId: binIdParam } = useParams<{
    variantId?: string;
    binId?: string;
  }>();
  const searchBinsByText = useSearchBinsByText();
  const { inv_inventoryDateLabel, ap_excludeRecalledHold } = useClientConfig();

  const getBinsIndex = useAppSelector(
    (state) => state.manualInventory.getBinsIndex
  );
  const isAddPanelOpen = useAppSelector(
    (state) => state.manualInventory.isAddPanelOpen
  );
  const isAdjustPanelOpen = useAppSelector(
    (state) => state.manualInventory.isAdjustPanelOpen
  );
  const isBinHoldModalOpen = useAppSelector(
    (state) => state.manualInventory.isBinHoldModalOpen
  );
  const inventorySummaryToDisplay = useAppSelector(
    selectInventorySummaryToDisplay
  );
  const isMovePanelOpen = useAppSelector(
    (state) => state.inventoryNew.isMovePanelOpen
  );
  const selectedRows = useAppSelector(
    (state) => state.manualInventory.selectedRows
  );
  const selectedSummaries = useAppSelector(
    (state) => state.manualInventory.selectedSummaries
  );
  const inventorySummaries = useAppSelector(
    (state) => state.inventory.inventorySummaries
  );
  const loadingInventorySummaries = useAppSelector(
    (state) => state.inventory.loadingInventorySummaries
  );
  const inventorySummariesCount = useAppSelector(
    (state) => state.inventory.inventorySummariesCount
  );
  const loadingBin = useAppSelector((state) => state.inventory.loadingBin);
  const loadingSlottingInfo = useAppSelector(
    (state) => state.inventory.loadingSlottingInfo
  );
  const selectedBin = useAppSelector((state) => state.inventory.bin);
  const slotBin = useAppSelector((state) => state.inventory.slotBin);
  const selectedVariant = useAppSelector(
    (state) => state.inventory.selectedVariant
  );
  const slottingInfo = useAppSelector((state) => state.inventory.slottingInfo);
  const clientId = useAppSelector((state) =>
    getUserClientId(state.login.profile)
  );
  const inventoryReports = useAppSelector(
    (state) => state.inventory.inventoryReport
  );

  const [scannedBarcode, setScannedBarcode] = useState<string | null>(null);
  // edit slotted bin
  const [newBinSlotModalOpen, setNewBinSlotModalOpen] =
    useState<boolean>(false);
  const [newBinSlotInputValue, setNewBinSlotInputValue] = useState<
    string | null
  >(null);
  const [newBinSlotAutocomplete, setNewBinSlotAutcomplete] = useState<
    SearchBinRecord[]
  >([]);
  const [selectedBinSlot, setSelectedBinSlot] =
    useState<SearchBinRecord | null>(null);
  const [scanChoices, setScanChoices] = useState<BinOrProductResult[]>(
    [] as BinOrProductResult[]
  );
  const [showScanChoiceModal, setShowScanChoiceModal] =
    useState<boolean>(false);
  const [isNavigateAwayModalOpen, setIsNavigateAwayModalOpen] = useState(false);
  const [page, setPage] = useState<number>(1);

  const [scanState, setScanState] = useScanIndicator();
  const isMobile = useMediaQuery(autostoreTheme.breakpoints.down("sm"));

  const limit = 10;
  const offset = (page - 1) * limit;
  const totalPageCount = inventorySummariesCount
    ? Math.ceil(inventorySummariesCount / limit)
    : 0;

  const viewType = getViewType(pathname);
  const isBinView = viewType === "bin";
  const isProductView = viewType === "product";
  const isProductsView = viewType === "products";

  const isAnySelect: boolean = selectedRows.length >= 1;

  let selectedLocationName = "";
  if (inventorySummaryToDisplay) {
    selectedLocationName =
      inventorySummaryToDisplay.binType === "autostore"
        ? "Autostore"
        : generateLocationNameFromInventorySummary(inventorySummaryToDisplay);
  }

  const selectedInventoryToModify: InventoryToModify = inventorySummaryToDisplay
    ? {
        binType: inventorySummaryToDisplay.binType,
        binId: inventorySummaryToDisplay.binId,
        inventoryId: inventorySummaryToDisplay.inventoryId,
        locationName: selectedLocationName,
        count: inventorySummaryToDisplay.count,
        committedCount: inventorySummaryToDisplay.committedCount,
        expiration: inventorySummaryToDisplay.expiration,
        manufactureDate: inventorySummaryToDisplay.manufactureDate,
        lotNumber: inventorySummaryToDisplay.lotNumber,
        variantId: inventorySummaryToDisplay.variantId
      }
    : null;

  const binConfiguration = availableBinConfigurations?.find(
    (config) =>
      config.configurationId === selectedBin?.autostoreBin?.binConfigurationId
  );
  const binHasCompartments =
    selectedBin?.binType === "autostore" &&
    (binConfiguration?.numberOfCompartments || 0) > 1;

  const clearSelectedData = useCallback(() => {
    dispatch(clearSelectedVariant());
    dispatch(clearSelectedInventoryId());
  }, [dispatch]);

  const resetView = (resetInv = true) => {
    if (resetInv) {
      clearSelectedData();
      dispatch(clearInventory());
    }
  };

  // component did unmount
  useEffect(
    () => () => {
      resetView();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (
      pathname === "/manual-inventory/bin" ||
      pathname === "/manual-inventory/product"
    ) {
      dispatch(clearSelectedVariant());
      dispatch(clearSelectedInventoryId());
      dispatch(clearInventory());
    }

    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleGetInventorySummaries = useCallback(
    (args: { variantId?: Guid; binId?: Guid; offsetZero?: boolean }) => {
      const { variantId, binId, offsetZero } = args;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- TODO: await this
      dispatch(
        getInventorySummaries({
          variantId,
          binId,
          limit,
          offset: offsetZero ? 0 : offset
        })
      );
    },
    [dispatch, offset]
  );

  // * fetch inventory, etc when url changes
  useEffect(() => {
    if (variantIdParam) {
      handleGetInventorySummaries({
        variantId: variantIdParam
      });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- TODO: await this
      dispatch(getSlottingInfo({ variantId: variantIdParam }));
    } else if (binIdParam) {
      handleGetInventorySummaries({ binId: binIdParam, offsetZero: true });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- TODO: await this
      dispatch(getBinById(binIdParam));
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- TODO: await this
      dispatch(getSlottingInfo({ binId: binIdParam }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantIdParam, binIdParam]);

  const handleRefetchInventory = useCallback(() => {
    if (variantIdParam) {
      handleGetInventorySummaries({ variantId: variantIdParam });
    } else if (binIdParam) {
      handleGetInventorySummaries({ binId: binIdParam });
    } else {
      handleGetInventorySummaries({});
    }
  }, [binIdParam, handleGetInventorySummaries, variantIdParam]);

  // fetch inventory when page changes (pagination)
  useEffect(() => {
    handleRefetchInventory();
  }, [limit, page, handleRefetchInventory]);

  // get variant info for selected inventory, or variant from params if no inventory records
  const searchVariantId: Guid | null =
    inventorySummaryToDisplay?.variantId || variantIdParam || null;

  useEffect(() => {
    if (searchVariantId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- TODO: await this
      dispatch(getVariantByVariantId(searchVariantId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVariantId]);

  // slotting
  const firstSlottingInfo = !!slottingInfo.length && slottingInfo[0];

  // get bin data for product slot to display bin location
  useEffect(() => {
    // get bin for most recent/last slot of slotting info
    if (isProductView && firstSlottingInfo) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- TODO: await this
      dispatch(getSlotBinById(firstSlottingInfo.binId));
    } else {
      dispatch(clearSlotBin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType, slottingInfo]);

  // get inventory report for selected variant. Used for total qty/total committed
  useEffect(() => {
    if (selectedVariant) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- TODO: await this
      dispatch(getInventoryReport(selectedVariant.variantId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant]);

  const autocompleteBinSearch = async (input: string | null): Promise<void> => {
    // search bins
    if (!input) {
      return;
    }
    if (input.length) {
      const binSearch = await searchBinsByText(input.replace("*", ""));
      if (binSearch.length === 1) {
        setNewBinSlotAutcomplete([]);
        setNewBinSlotInputValue(null);
        setSelectedBinSlot(binSearch[0]);
      } else {
        setNewBinSlotAutcomplete(binSearch);
      }
    }
  };

  const confirmBinSlotUpdate = async () => {
    if (selectedBinSlot && firstSlottingInfo) {
      await dispatch(
        updateSlottingInfo({
          slotId: firstSlottingInfo.slotId,
          binId: selectedBinSlot.bin_id
        })
      ).then(async () => {
        await dispatch(getSlottingInfo({ variantId: variantIdParam }));
        setSelectedBinSlot(null);
      });
    }
  };

  const confirmCreateBinSlot = async () => {
    if (selectedBinSlot && variantIdParam) {
      await dispatch(
        createBinSlot({
          binId: selectedBinSlot.bin_id,
          variantId: variantIdParam
        })
      ).then(async () => {
        await dispatch(getSlottingInfo({ variantId: variantIdParam }));
        setSelectedBinSlot(null);
      });
    }
  };

  const handleAutocompleteSelect = useCallback(
    (option: BinOrProductResult | null): void => {
      setShowScanChoiceModal(false);

      // don't redirect to the same page if same search result is selected
      if (option && option.variantId && pathname.includes(option.variantId)) {
        return;
      }

      if (option && option.type !== "bin") {
        clearSelectedData();
        navigate({
          pathname: `/manual-inventory/product/${option?.variantId ?? ""}`,
          search
        });
      } else if (option?.type === "bin") {
        clearSelectedData();
        navigate({
          pathname: `/manual-inventory/bin/${option?.binId ?? ""}`,
          search
        });
      }
    },
    [clearSelectedData, navigate, pathname, search]
  );

  const { setToolbar } = useNavbar({
    centerComponent: useMemo(
      () => (
        <ManualInventorySearch
          handleAutocompleteSelect={handleAutocompleteSelect}
        />
      ),
      [handleAutocompleteSelect]
    ),
    viewTitle
  });

  useKeyDownHandler();
  interface ScanObj {
    scanType:
      | "search bin record"
      | "bin result"
      | "product result"
      | "products result";
    searchBinRecord?: SearchBinRecord;
    binOrProductResult?: BinOrProductResult;
    productsResult?: BinOrProductResult[];
  }

  useBarcodeScanner<ScanObj | null>({
    disabled: isMovePanelOpen,
    findScanMatch: async (buffer: string) => {
      if (newBinSlotModalOpen) {
        const binSearch = await searchBinsByText(buffer.replace("*", ""));
        if (binSearch.length === 1) {
          return {
            scanType: "search bin record",
            searchBinRecord: binSearch[0]
          };
        }
      } else {
        setScannedBarcode(buffer);
        setScanState("success");
        const hits = clientId ? await searchProduct(buffer) : [];
        const hitsAsAutocompleteRecords = createProductSearchOptions({
          hits,
          exactUpcMatchFilter: buffer
        });
        if (hitsAsAutocompleteRecords.length === 1) {
          return {
            scanType: "product result",
            binOrProductResult: hitsAsAutocompleteRecords[0]
          };
        }
        if (hitsAsAutocompleteRecords.length > 1) {
          return {
            scanType: "products result",
            productsResult: hitsAsAutocompleteRecords
          };
        }

        // if search product returns no results, search bins
        const binSearch = await warehouseService.get<SearchBinRecord[]>(
          `/api/bins/search/${buffer}`
        );
        const binRecords = binSearch.data;

        const binsAsAutocompleteRecords = createBinSearchOptions(binRecords);
        if (binsAsAutocompleteRecords.length === 1) {
          return {
            scanType: "bin result",
            binOrProductResult: binsAsAutocompleteRecords[0]
          };
        }
      }
      return null;
    },
    processScanMatch: (scanObj) => {
      if (!scanObj) return null;
      if (isAddPanelOpen) return null;
      if (scanObj.scanType === "products result" && scanObj.productsResult) {
        setScanChoices(scanObj.productsResult);
        setShowScanChoiceModal(true);
        return null;
      }

      const matchedItem = scanObj.searchBinRecord || scanObj.binOrProductResult;
      if (!matchedItem) {
        return null;
      }
      setScanState("success");

      if (scanObj.scanType === "search bin record") {
        setNewBinSlotAutcomplete([]);
        setNewBinSlotInputValue(null);
        setSelectedBinSlot(matchedItem as SearchBinRecord);
      } else if (scanObj.scanType === "product result") {
        navigate({
          pathname: `/manual-inventory/product/${
            (matchedItem as BinOrProductResult).variantId as string
          }`,
          search
        });
      } else if (scanObj.scanType === "bin result") {
        dispatch(clearSelectedVariant());
        navigate({
          pathname: `/manual-inventory/bin/${
            (matchedItem as BinOrProductResult).binId as string
          }`,
          search
        });
      }
      return null;
    },
    deps: [viewType, newBinSlotModalOpen]
  });

  useEffect(() => {
    if (getBinsIndex !== 0) {
      dispatch(setGetBinsIndex(0));
    }
  });

  const resetPage = useCallback(() => {
    dispatch(clearSelectedVariant());
    dispatch(clearInventory());
    dispatch(selectRows([]));
    dispatch(selectSummaries([]));
    handleRefetchInventory();
    setPage(1);
  }, [dispatch, handleRefetchInventory]);

  const softReset = () => {
    clearInventoryToModify();
    dispatch(setIsAddPanelOpen(false));
    dispatch(setIsAdjustPanelOpen(false));
  };

  // reset state and refetch inventory summaries
  useEffect(() => {
    resetPage();
  }, [resetPage]);

  function NoInventoryFound() {
    return (
      <Typography variant="h5" align="center">
        {t("no inventory found")}
      </Typography>
    );
  }

  const handleScanChoiceSelect = (value: string) => {
    const selectedProductResult: BinOrProductResult | undefined =
      scanChoices.find((scanChoice) => scanChoice.variantId === value);

    if (selectedProductResult) handleAutocompleteSelect(selectedProductResult);
  };

  const handleFetchAllInventory = () => {
    clearSelectedData();
    resetPage();
    navigate({
      pathname: `/manual-inventory/product`,
      search
    });
    handleGetInventorySummaries({ offsetZero: true });
  };

  useEffect(() => {
    if (selectedRows.length) {
      setToolbar(
        <ManualInventoryToolbar
          handleGetInventorySummaries={handleGetInventorySummaries}
          resetPage={resetPage}
        />
      );
    } else {
      setToolbar(undefined);
    }
  }, [handleGetInventorySummaries, resetPage, selectedRows.length, setToolbar]);

  return (
    <>
      <Container maxWidth="xl" sx={{ padding: 2 }}>
        <Grid container spacing={2}>
          <Grid item md={4} xs={12}>
            <InventoryProductCard
              variantId={
                isAnySelect || isProductView
                  ? selectedVariant?.variantId
                  : undefined
              }
              isInventorySelected={isAnySelect}
              addClickCb={() => dispatch(setIsAddPanelOpen(true))}
              inventoryReports={inventoryReports}
              selectedInventorySummary={inventorySummaryToDisplay}
              initialCycleCountFrequency={null}
              hideAddInventory={selectedRows.length > 1}
            />
          </Grid>

          <Grid item container xs={12} md={8}>
            <Grid item xs={12}>
              <Paper>
                <Grid container justifyContent="flex-end">
                  <Box style={{ margin: "15px 5px 5px" }}>
                    <Button onClick={handleFetchAllInventory}>
                      <Typography>{t("return to all inventory")}</Typography>
                    </Button>
                  </Box>
                </Grid>
                <Box padding="15px">
                  <Grid>
                    {/* product view - top of table */}
                    {isProductView && selectedVariant && (
                      <Grid item xs={12}>
                        <Typography variant="h6" component="div">
                          {getVariantDisplayNameByDtoFe(selectedVariant)}
                        </Typography>
                        <Box minHeight="36px">
                          {loadingBin ||
                            (loadingSlottingInfo && (
                              <Skeleton height={36} width={130} />
                            ))}
                          {!loadingBin &&
                            !loadingSlottingInfo &&
                            (slotBin ? (
                              <Grid container>
                                <Typography
                                  component="div"
                                  sx={{ color: "darkGray.main" }}
                                >
                                  {`${t(
                                    "slotted bin"
                                  )}: ${generateLocationNameFromBin(slotBin)}`}
                                </Typography>
                                {!!slottingInfo.length && (
                                  <IconButton
                                    aria-label="edit"
                                    style={{ padding: 0 }}
                                    onClick={() => setNewBinSlotModalOpen(true)}
                                    size="large"
                                  >
                                    <EditIcon
                                      sx={{
                                        color: "success.main",
                                        fontSize: 20,
                                        paddingLeft: 2,
                                        paddingBottom: 2
                                      }}
                                    />
                                  </IconButton>
                                )}
                              </Grid>
                            ) : (
                              <Button
                                sx={{ color: "success.main" }}
                                variant="text"
                                startIcon={<Add />}
                                size="large"
                                onClick={() => {
                                  setNewBinSlotModalOpen(true);
                                }}
                              >
                                {t("assign slot")}
                              </Button>
                            ))}
                        </Box>
                      </Grid>
                    )}
                    {/* bin view - top of table */}
                    {isBinView && selectedBin && (
                      <Grid item xs={12}>
                        <Box display="flex">
                          <Typography
                            variant="h6"
                            role="heading"
                            component="div"
                            marginRight={1}
                          >
                            {generateLocationNameFromBin(selectedBin)}
                          </Typography>
                          {!!binHasCompartments &&
                            !!selectedBin.autostoreCompartmentNumber && (
                              <Chip
                                label={
                                  `${t("compartment")}` +
                                  ` ${selectedBin.autostoreCompartmentNumber}`
                                }
                                sx={{
                                  backgroundColor: "primary.main",
                                  color: "primary.contrastText"
                                }}
                              />
                            )}
                        </Box>
                        {selectedBin &&
                          selectedBin.binType.toLowerCase() !== "autostore" && (
                            <Grid container>
                              {firstSlottingInfo && (
                                <Typography
                                  component="div"
                                  sx={{ color: "gray.dark" }}
                                >
                                  {`${t("slotted product")}: ${
                                    firstSlottingInfo.name
                                  }`}
                                </Typography>
                              )}
                            </Grid>
                          )}
                      </Grid>
                    )}
                    {!loadingInventorySummaries &&
                      inventorySummaries &&
                      !inventorySummaries.length && (
                        <Grid item xs={12}>
                          <NoInventoryFound />
                        </Grid>
                      )}
                  </Grid>
                </Box>
                {/* inventory bin table */}
                {((!loadingInventorySummaries &&
                  inventorySummaries &&
                  inventorySummaries.length > 0) ||
                  loadingInventorySummaries) && (
                  <AutostoreTable<InventorySummaryDto>
                    widthOfCols={
                      !isMobile
                        ? ["30%", "20%", "10%", "10%", "10%", "20%"]
                        : ["40%", "20%", "20%"]
                    }
                    headerVariant="overline"
                    bodyVariant="body2"
                    headerColNames={
                      !isMobile
                        ? [
                            t("product"),
                            t("bin"),
                            t("quantity"),
                            t("hold type"),
                            t("committed"),
                            t(inventoryDateLabel(inv_inventoryDateLabel))
                          ]
                        : [
                            isBinView || isProductsView
                              ? t("product")
                              : t("bin"),
                            t("quantity"),
                            t(inventoryDateLabel(inv_inventoryDateLabel))
                          ]
                    }
                    rowId={(row: InventorySummaryDto) => row.inventoryId}
                    renderColumns={[
                      (row: InventorySummaryDto) => {
                        let firstColumnData = row.productName;
                        if (isMobile && isProductView) {
                          firstColumnData =
                            row.binType === "autostore"
                              ? `${row.autostoreBinNumber || ""}`
                              : generateLocationNameFromInventorySummary(row);
                        }
                        return (
                          <div>
                            <Typography variant="body2">
                              {firstColumnData}
                            </Typography>
                            <Grid sx={{ marginTop: 1 }}>
                              {!!row.temperatureZone && (
                                <Chip
                                  label={t(
                                    `${row.temperatureZone.toLowerCase() as TempZone}`
                                  )}
                                  sx={{
                                    bgcolor:
                                      temperatureZoneColors[
                                        row.temperatureZone.toLowerCase() as TempZone
                                      ],
                                    color:
                                      row.temperatureZone.toLowerCase() +
                                      ".contrastText",
                                    marginRight: 1
                                  }}
                                />
                              )}
                              <Chip
                                label={row.binType}
                                sx={{
                                  textTransform: "capitalize"
                                }}
                              />
                            </Grid>
                          </div>
                        );
                      },
                      isMobile
                        ? null
                        : (row) => {
                            let secondColumn = "";
                            if (row.binType === "autostore") {
                              secondColumn = `${row.autostoreBinNumber || ""}`;
                            } else {
                              secondColumn =
                                generateLocationNameFromInventorySummary(row);
                            }
                            return (
                              <div>
                                <Typography variant="body2">
                                  {secondColumn}
                                </Typography>
                                <Grid sx={{ marginTop: 1 }}>
                                  {!!row.autostoreCompartmentNumber && (
                                    <Chip
                                      label={`${t("compartment")} ${
                                        row.autostoreCompartmentNumber
                                      }`}
                                      sx={{
                                        backgroundColor: "primary.main",
                                        color: "primary.contrastText",
                                        marginRight: 1
                                      }}
                                    />
                                  )}
                                </Grid>
                              </div>
                            );
                          },
                      (row: InventorySummaryDto) =>
                        `${row.count?.value !== undefined ? row.count?.value : t("n/a")} ${row.count?.units || ""}`,
                      isMobile
                        ? null
                        : (row: InventorySummaryDto) => {
                            return (
                              <Box>
                                {row.holds?.map((hold, index) => (
                                  <Box key={`hold-${index}`}>
                                    <Typography>
                                      {t(
                                        hold.reasonCode.toLowerCase() as InventoryHoldReasonCode
                                      )}
                                    </Typography>
                                  </Box>
                                )) || ""}
                              </Box>
                            );
                          },
                      isMobile
                        ? null
                        : (row: InventorySummaryDto) =>
                            `${row.committedCount?.value !== undefined ? row.committedCount?.value : ""} ${row.committedCount?.units || ""}`,
                      (row: InventorySummaryDto) =>
                        formatUtcDate(
                          checkIsExpiration(inv_inventoryDateLabel)
                            ? row.expiration
                            : row.manufactureDate
                        )
                    ]}
                    rowData={inventorySummaries}
                    selectedRows={selectedRows}
                    selectRowCallback={(row: InventorySummaryDto) => {
                      const rowId = row.inventoryId;
                      if (selectedRows.includes(rowId)) {
                        dispatch(
                          selectRows(selectedRows.filter((id) => id !== rowId))
                        );
                        dispatch(
                          selectSummaries(
                            selectedSummaries.filter(
                              (summary) => summary?.inventoryId !== rowId
                            )
                          )
                        );
                        if (
                          selectedRows.length === 1 &&
                          (isBinView || isProductsView)
                        ) {
                          dispatch(clearSelectedVariant());
                        }
                      } else {
                        dispatch(selectRows([...selectedRows, rowId]));
                        dispatch(
                          selectSummaries([
                            ...selectedSummaries,
                            inventorySummaries.find(
                              (summary) => summary.inventoryId === rowId
                            ) || null
                          ])
                        );
                      }
                    }}
                    loading={loadingInventorySummaries}
                  />
                )}
              </Paper>
            </Grid>
            {totalPageCount > 1 && (
              <Grid container justifyContent="center" style={{ marginTop: 16 }}>
                <Pagination
                  count={totalPageCount}
                  page={page}
                  onChange={(_e, p) => {
                    setPage(p);
                    if (window.scrollTo) window.scrollTo(0, 0);
                  }}
                  shape="rounded"
                />
              </Grid>
            )}
          </Grid>

          <InventoryAddDialog
            onClose={() => {
              softReset();
            }}
            open={isAddPanelOpen}
            variant={selectedVariant || undefined}
            autostorePortId={null}
            refreshCb={async () => {
              handleGetInventorySummaries({
                variantId: variantIdParam
              });
              if (variantIdParam)
                await dispatch(getInventoryReport(variantIdParam));
            }}
            disableClosePortOnClose
          />

          <InventoryAdjustDialog
            open={isAdjustPanelOpen}
            onClose={() => {
              softReset();
            }}
            invToModify={selectedInventoryToModify}
            selectedVariant={selectedVariant || null}
            refreshCb={async () => {
              dispatch(setIsAdjustPanelOpen(false));
              if (selectedVariant) {
                await dispatch(getInventoryReport(selectedVariant?.variantId));
                dispatch(selectRows([]));
                dispatch(selectSummaries([]));
                handleRefetchInventory();
              }
            }}
            canceledReason=""
            isAdjustOnMain
          />
        </Grid>
        <Dialog
          key="new-bin-dialog"
          open={newBinSlotModalOpen}
          onClose={() => {
            setNewBinSlotModalOpen(false);
          }}
        >
          <DialogTitle>{slotBin ? "Update Slot" : "Assign Slot"}</DialogTitle>
          <DialogContent style={{ textAlign: "center" }}>
            <FormControl style={{ margin: 10 }}>
              <Autocomplete
                id="combo-box-demo"
                options={newBinSlotAutocomplete}
                filterOptions={(ops) => ops}
                getOptionLabel={(option) => option.location}
                style={{ width: 220 }}
                open={!!newBinSlotAutocomplete.length}
                onChange={(_e, option) => {
                  setNewBinSlotInputValue(null);
                  setSelectedBinSlot(option);
                }}
                inputValue={
                  newBinSlotInputValue || selectedBinSlot?.location || ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    type="text"
                    label={t("scan new bin")}
                    onChange={async (
                      e: React.ChangeEvent<{ value: unknown }>
                    ) => {
                      setSelectedBinSlot(null);
                      setNewBinSlotInputValue(
                        (e.target.value as string).replace("*", "")
                      );
                      await autocompleteBinSearch(e.target.value as string);
                    }}
                  />
                )}
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              autoFocus
              onClick={() => {
                setNewBinSlotAutcomplete([]);
                setNewBinSlotInputValue(null);
                setNewBinSlotModalOpen(false);
              }}
              color="primary"
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                if (slotBin && firstSlottingInfo) {
                  await confirmBinSlotUpdate();
                } else {
                  await confirmCreateBinSlot();
                }
                setNewBinSlotAutcomplete([]);
                setNewBinSlotInputValue(null);
                setNewBinSlotModalOpen(false);
              }}
              color="primary"
              autoFocus
              disabled={!selectedBinSlot}
            >
              {t("confirm")}
            </Button>
          </DialogActions>
        </Dialog>
        <BigChoiceModal
          isOpen={showScanChoiceModal}
          choiceTitle="Select Variant"
          choices={scanChoices.map((scanChoice) => ({
            label: scanChoice.displayText,
            value: scanChoice.variantId || ""
          }))}
          selectCb={(selected) => {
            handleScanChoiceSelect(selected);
          }}
          closeCb={() => setShowScanChoiceModal(false)}
        />
        <ConfirmationModal
          isOpen={isNavigateAwayModalOpen}
          confirmCb={() => {
            setIsBinComponentShown(false);
            dispatch(setPortPollingActive(false));
            setIsNavigateAwayModalOpen(false);
            resetPage();
          }}
          closeCb={() => {
            setIsNavigateAwayModalOpen(false);
          }}
          modalTitle={t("are you sure you want to close the workstation")}
          modalText=""
          labelledById="close-workstation-modal-title"
        />
        {/* Change to modify multiple rows to the same hold status */}
        {isAnySelect && (
          <InventoryHoldModal
            confirmCb={async (holdType: HoldType) => {
              await Promise.any(
                selectedRows.map(async (rowId: string) => {
                  await dispatch(
                    placeInventoryHold(rowId, holdType.reasonCode, null)
                  );
                })
              );
              // if we are not adjusting bins & update hold, we want to reset the page.
              dispatch(selectRows([]));
              dispatch(selectSummaries([]));
              handleRefetchInventory();
              dispatch(setIsBinHoldModalOpen(false));
            }}
            open={isBinHoldModalOpen}
            closeModalCallback={() => dispatch(setIsBinHoldModalOpen(false))}
            maxWidth="sm"
            options={getHoldTypeOptions(ap_excludeRecalledHold)}
            confirmButtonText={t("set hold")}
            currentHold=""
          />
        )}
        <ScanningIndicator
          scanState={scanState}
          scannedBarcode={scannedBarcode}
          placeholderText="Scan Product or Bin Barcode"
        />
      </Container>
    </>
  );
}
